/* Stripe return pages. Checkout sends the customer back here; we poll our own status
   endpoint rather than trusting the redirect, then let them back into the plant. */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { CheckCircle2, Loader2, XCircle, ArrowRight } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking");   // checking | paid | pending | error
  const tries = useRef(0);

  const poll = useCallback(async () => {
    if (!sessionId) return setState("error");
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") return setState("paid");
      if (["failed", "expired"].includes(data.payment_status)) return setState("error");
      tries.current += 1;
      if (tries.current > 20) return setState("pending");
      setTimeout(poll, 2000);
    } catch {
      setState("error");
    }
  }, [sessionId]);
  useEffect(() => { poll(); }, [poll]);

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6 py-12" data-testid="payment-success">
      <div className="max-w-lg">
        {state === "checking" && (
          <>
            <Loader2 className="w-9 h-9 text-zinc-400 animate-spin" />
            <h1 className="mt-5 text-3xl font-black tracking-tight text-zinc-900">Confirming your payment…</h1>
            <p className="mt-3 text-base text-zinc-600">One moment — don't close this window.</p>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="w-10 h-10 text-emerald-600" data-testid="payment-paid" />
            <h1 className="mt-5 text-3xl sm:text-4xl font-black tracking-tight text-zinc-900">You're subscribed.</h1>
            <p className="mt-4 text-base text-zinc-600">
              Your plant is unlocked and the receipt is on its way to your email. Thanks — welcome aboard.
            </p>
            <Link to="/" data-testid="payment-success-continue"
              className="mt-8 inline-flex items-center gap-2 bg-[#1D4ED8] hover:bg-[#1A43BA] text-white text-base font-bold px-7 py-3.5 transition-colors">
              Go to my plant <ArrowRight className="w-4 h-4" />
            </Link>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="w-9 h-9 text-amber-500" />
            <h1 className="mt-5 text-3xl font-black tracking-tight text-zinc-900">Still processing.</h1>
            <p className="mt-4 text-base text-zinc-600">
              Your bank hasn't confirmed yet. It usually lands within a minute or two — refresh this page,
              or email admin@armorbatch.com if it hasn't cleared shortly.
            </p>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-10 h-10 text-red-600" />
            <h1 className="mt-5 text-3xl font-black tracking-tight text-zinc-900">We couldn't confirm that payment.</h1>
            <p className="mt-4 text-base text-zinc-600">
              Nothing has been charged twice. Email admin@armorbatch.com and we'll sort it out.
            </p>
            <Link to="/" className="mt-8 inline-flex items-center gap-2 border border-zinc-300 text-zinc-800 text-sm font-bold uppercase tracking-widest px-6 py-3.5">
              Back
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-6 py-12" data-testid="payment-cancel">
      <div className="max-w-lg">
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-zinc-900">Payment cancelled.</h1>
        <p className="mt-4 text-base text-zinc-600">
          No charge was made. You can pick it back up any time from Settings, or talk to us first —
          admin@armorbatch.com.
        </p>
        <Link to="/settings" data-testid="payment-cancel-back"
          className="mt-8 inline-flex items-center gap-2 bg-zinc-900 hover:bg-black text-white text-sm font-bold uppercase tracking-widest px-6 py-3.5 transition-colors">
          Back to settings
        </Link>
      </div>
    </div>
  );
}
